import React, { useCallback, useState } from 'react'
import { Card, Col, Dropdown, Row } from 'react-bootstrap'
import './PackagesPage.scss'
import PackageList from './PackageList/PackageList'
import AddPackageModal from '../../components/Modals/AddPackageModal'
import PackageModel from '@geartrack/shared-lib/dist/PackageModel'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import GoogleAd from '../../components/GoogleAd'
import PackageInformationBody from './PackageTimelines/PackageInformationBody'
import usePackagesRepository from '../../hooks/usePackagesRepository'
import { usePackages } from '../../components/PackagesProvider'
import { useLoadAdsenseScript } from '../../hooks/util/useExternalScript'
import SharePackageModal from '../../components/Modals/SharePackageModal'
import SuggestCarrierModal from '../../components/Modals/SuggestCarrierModal'

const PackagesPage = () => {
  const packages = usePackages()

  useLoadAdsenseScript()

  return (
    <div className="packages-page">
      <Row>
        <Col md={5} lg={4}>
          <PackageList packages={packages} />

          {/*
           // @ts-ignore */}
          <div className="mt-3 text-center" align="center">
            <GoogleAd
              client="ca-pub-3952596231252607"
              slot="3564793884"
              data-full-width-responsive="false"
              className="ad_below_package_list"
              autoFormat={false}
              styles={{
                display: 'block',
              }}
            />
          </div>
        </Col>
        <Col md={7} lg={8} className="mt-3 mt-md-0">
          <PackageTimelines packages={packages} />
        </Col>
      </Row>

      <AddPackageModal />
      <SharePackageModal />
      <SuggestCarrierModal />
    </div>
  )
}

const PackageTimelines = React.memo(({ packages }: { packages: PackageModel[] }) => {
  const { t } = useTranslation('packages')
  const pkgsRepo = usePackagesRepository()

  const onCollapse = useCallback(
    (pkg: PackageModel) => {
      pkgsRepo.toggleCollapsePackage(pkg)
    },
    [pkgsRepo]
  )

  if (packages.length === 0) {
    return <div>{t('timeline.no packages')}</div>
  }

  return (
    <>
      {packages.map((pkg, i) => (
        <div className={classNames(i < packages.length - 1 ? 'mb-3' : '')} key={pkg.computedKey}>
          <Row>
            <Col>
              <PackageInformationCard pkg={pkg} onCollapse={onCollapse} />
            </Col>
          </Row>
          {shouldPlaceAd(i, pkg.collapsed) && (
            <>
              {/*
               // @ts-ignore */}
              <div className="mt-3 text-center" align="center">
                <GoogleAd
                  client="ca-pub-3952596231252607"
                  slot="1856960541"
                  data-full-width-responsive="false"
                  className="ad_between_packages"
                  autoFormat={false}
                  styles={{
                    display: 'block',
                  }}
                />
              </div>
            </>
          )}
        </div>
      ))}
    </>
  )
})

export type PackageInformationCardProps = { pkg: PackageModel; onCollapse?: (pkg: PackageModel) => void }

export const PackageInformationCard = React.memo(({ pkg, onCollapse }: PackageInformationCardProps) => {
  const [isLoading, setLoading] = useState(true)

  const onClick = useCallback(() => {
    if (onCollapse) {
      onCollapse(pkg)
    }
  }, [onCollapse, pkg])

  return (
    <Card className="tracking-object" id={pkg.computedKey}>
      <Card.Header
        className={classNames({ pointer: Boolean(onCollapse) }, 'bg-secondary text-light d-flex')}
        onClick={onClick}
      >
        <div className="flex-grow-1 text-truncate">
          {!pkg.isDescriptionEmpty() ? (
            <>
              <h3 className="card-title d-inline">
                <i className="fas fa-box-alt me-2 fa-lg" />
                {pkg.description}
              </h3>
              <span className="ms-2">{pkg.trackingId}</span>
            </>
          ) : (
            <>
              <h3 className="card-title d-inline">
                <i className="fas fa-box-alt me-2 fa-lg" />
                {pkg.trackingId}
              </h3>
              <span className="ms-2">{pkg.trackingId}</span>
            </>
          )}

          {pkg.extraData ? <span className="ms-2 text-muted">{pkg.getExtraDataString()}</span> : null}
        </div>
        <div>{!pkg.collapsed && isLoading ? <i className="fas fa-circle-notch fa-spin" /> : ''}</div>
      </Card.Header>
      <Card.Body className="p-0">
        {!pkg.collapsed && (
          <div className="px-2 py-2">
            <PackageInformationBody pkg={pkg} setLoading={setLoading} />
          </div>
        )}
      </Card.Body>
    </Card>
  )
})

export const PackageOptionsCard = React.memo(({ pkg }: { pkg: PackageModel }) => {
  return (
    <Dropdown onClick={e => e.stopPropagation()}>
      <Dropdown.Toggle variant="dark" id={'dropdown-package-card-' + pkg.computedKey}>
        <i className="fas fa-cog" />
      </Dropdown.Toggle>

      <Dropdown.Menu>
        <Dropdown.Item>
          <i className="fas fa-edit mr-2" />
          Some options that we might need
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  )
})

function shouldPlaceAd(i, collapsed) {
  // we don't want to show an Ad if we're not showing the order details
  if (collapsed) return false

  // not a uniform distribution, hand-picked to not show too many ads
  return [1, 3, 6, 9, 12].includes(i)
}

export default PackagesPage
